import { useCallback } from 'react';
import { Flow } from './types';

/**
 * Generic hook for implementing dynamic step navigation with skip evaluation.
 * Can be used by any flow that needs intelligent step progression.
 *
 * @template TVariables - GraphQL query variables type (must include nodeId: string)
 * @template TSkipData - GraphQL query result type for skip evaluation data
 *
 * @param flow - The flow definition with steps
 * @param fetchSkipData - GraphQL lazy query function for fetching skip evaluation data
 * @param includeVariables - Variables for @include directives in the GraphQL query
 * @param nodeId - OneGraph node ID for the current application state
 * @param nodeIdLoading - Loading state from useNodeId hook
 *
 * @returns getNextStep function and loading state
 */
export const useGetNextStep = <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    TVariables extends { nodeId: string } & Record<string, any> = { nodeId: string } & Record<
        string,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        any
    >,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    TSkipData = any,
>(
    flow: Flow<TSkipData>,
    fetchSkipData: (options: { variables: TVariables }) => Promise<{ data?: TSkipData }>,
    includeVariables: Omit<TVariables, 'nodeId'>,
    nodeId: string | null,
    nodeIdLoading: boolean = false,
): {
    getNextStep: (currentRoute: string) => Promise<string | null>;
    loading: boolean;
} => {
    const getNextStep = useCallback(
        async (currentRoute: string): Promise<string | null> => {
            if (!currentRoute) {
                console.warn('[useGetNextStep]: No current step available');
                return null;
            }

            if (!nodeId) {
                console.warn('[useGetNextStep]: No nodeId available for skip evaluation');
                // Fallback to simple next step logic
                const currentIndex = flow.steps.findIndex((step) =>
                    step.routes.includes(currentRoute),
                );
                return currentIndex >= 0 && currentIndex < flow.steps.length - 1
                    ? flow.steps[currentIndex + 1].routes[0]
                    : null;
            }

            try {
                // Check if we have any remaining steps that might need skip evaluation
                const currentStepIndex = flow.steps.findIndex((step) =>
                    step.routes.includes(currentRoute),
                );

                let nextStepIndex: number;

                if (currentStepIndex === -1) {
                    console.log(
                        '[useGetNextStep]: Current step not in flow (e.g., index page), evaluating from start',
                    );
                    // When called from outside the flow (e.g., index page), start evaluating from the first step
                    nextStepIndex = 0;
                } else {
                    nextStepIndex = currentStepIndex + 1;
                }

                // Check if any remaining steps have canSkip functions
                const remainingSteps = flow.steps.slice(nextStepIndex);
                const hasSkippableSteps = remainingSteps.some((step) => step.canSkip);

                if (!hasSkippableSteps) {
                    // No steps have canSkip logic, use simple next step logic
                    return nextStepIndex < flow.steps.length
                        ? flow.steps[nextStepIndex].routes[0]
                        : null;
                }

                // Execute the lazy query with include variables
                // We've already checked nodeId is not null above, so it's safe to assert as string
                const { data } = await fetchSkipData({
                    variables: {
                        nodeId: nodeId, // Safe assertion - we checked !nodeId above
                        ...includeVariables,
                    } as TVariables,
                });

                if (!data) {
                    console.warn('[useGetNextStep]: No data returned from skip evaluation query');
                    return null;
                }

                // Evaluate ALL subsequent steps for skipping
                let evaluationIndex = nextStepIndex;

                while (evaluationIndex < flow.steps.length) {
                    const nextStep = flow.steps[evaluationIndex];
                    const shouldSkip = nextStep.canSkip ? nextStep.canSkip(data) : false;

                    if (!shouldSkip) {
                        // Found the next step that shouldn't be skipped
                        return nextStep.routes[0];
                    }

                    evaluationIndex++;
                }

                return null;
            } catch (error) {
                console.error('[useGetNextStep]: Error in getNextStep', error);

                // Fallback: simple next step logic
                const currentIndex = flow.steps.findIndex((step) =>
                    step.routes.includes(currentRoute),
                );

                if (currentIndex === -1) {
                    // Not in flow, return first step
                    return flow.steps.length > 0 ? flow.steps[0].routes[0] : null;
                } else {
                    // In flow, return next step
                    return currentIndex < flow.steps.length - 1
                        ? flow.steps[currentIndex + 1].routes[0]
                        : null;
                }
            }
        },
        [nodeId, includeVariables, fetchSkipData, flow.steps],
    );

    return {
        getNextStep,
        loading: nodeIdLoading,
    };
};
